import React from 'react';
import type { MusicalToken, RhythmReadingMethod, TimeSignature } from '../../types/music';
import type { RhythmicCell } from '../rhythmicCellEngine';
import { CELL_REGISTRY, lookupCellById } from '../rhythmicCellEngine';
import type { RhythmCellMcqOption } from '../rhythmCellMcq';
import { formatCellMcqLabel } from '../rhythmCellMcq';
import { VexRhythmGlyph } from './VexRhythmGlyph';
import { VexRhythmStrip } from './VexRhythmStrip';
import type { RhythmSlotFeedback } from '../../types/payloads/rhythm_canvas.types';

/** Células didácticas disponibles en la paleta del taller (sin duplicados pedagógicos). */
const WORKSHOP_PALETTE_IDS = [
  'NEGRA',
  'BLANCA',
  'CORCHEA_SUELTA',
  'SEMICORCHEA',
  'PAR_CORCHEAS',
  'GALOPA',
  'GALOPA_INV',
  'CUARTINA',
  'SALTILLO',
  'SALTILLO_INV',
  'TRESILLO_CORCHEAS',
  'SILENCIO_NEGRA',
] as const;

function workshopPalette(beatCells: RhythmicCell[]): RhythmicCell[] {
  const fromBar = beatCells.filter((c) => c.id !== 'COMPOUND_BEAT' && c.id !== 'SILENCIO_NEGRA');
  const registry = WORKSHOP_PALETTE_IDS.map((id) => lookupCellById(id)).filter(Boolean) as RhythmicCell[];
  const seen = new Set<string>();
  const merged: RhythmicCell[] = [];

  for (const cell of [...fromBar, ...registry]) {
    const key = cell.id;
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(cell);
  }
  return merged;
}

function cellToOptionLabel(cell: RhythmicCell, readingMethod: RhythmReadingMethod): string {
  return formatCellMcqLabel(cell, readingMethod);
}

function mcqOptionsToCells(options: RhythmCellMcqOption[]): RhythmicCell[] {
  return options
    .map((opt) => lookupCellById(opt.id))
    .filter(Boolean) as RhythmicCell[];
}

export interface BeatCellWorkshopGridProps {
  beatCells: RhythmicCell[];
  timeSignature?: TimeSignature;
  readingMethod?: RhythmReadingMethod;
  taskKind: 'complete' | 'transcribe' | 'syllables' | 'practice' | 'identify';
  blankIndices: number[];
  missingIndex?: number;
  dottedIndices?: number[];
  targetBarNumber?: number;
  graphicMode?: 'symbols' | 'symbols-labels';
  hidePattern?: boolean;
  /** Tokens planos para la tira Vex cuando no se oculta el patrón. */
  patternTokens?: MusicalToken[];
  values: Record<string, string>;
  feedback?: Record<string, RhythmSlotFeedback>;
  disabled?: boolean;
  onSlotChange: (index: number, value: string) => void;
  printMode?: boolean;
  /** Alternativas MCQ curadas para el casillero en blanco (complete). */
  cellOptions?: RhythmCellMcqOption[];
}

export function BeatCellWorkshopGrid({
  beatCells,
  timeSignature = '4/4',
  readingMethod = 'takadimi',
  taskKind,
  blankIndices,
  missingIndex,
  dottedIndices = [],
  targetBarNumber,
  graphicMode = 'symbols',
  hidePattern = false,
  patternTokens,
  values,
  feedback,
  disabled = false,
  onSlotChange,
  printMode = false,
  cellOptions,
}: BeatCellWorkshopGridProps) {
  const palette = workshopPalette(beatCells);
  const mcqPalette =
    cellOptions?.length && taskKind === 'complete'
      ? mcqOptionsToCells(cellOptions)
      : palette;
  const selectPalette =
    cellOptions?.length && taskKind === 'complete' && blankIndices.length === 1
      ? mcqPalette
      : palette;
  const isSyllables = taskKind === 'syllables';
  const showStrip =
    !hidePattern &&
    taskKind !== 'transcribe' &&
    (beatCells.length > 0 || (patternTokens?.length ?? 0) > 0);

  return (
    <div className={`beat-cell-workshop${printMode ? ' beat-cell-workshop--print' : ''}`}>
      {showStrip && (
        <VexRhythmStrip
          pattern={
            beatCells.length
              ? beatCells.flatMap((cell) => cell.tokens)
              : (patternTokens ?? beatCells.flatMap((cell) => cell.tokens))
          }
          beatCells={beatCells}
          timeSignature={timeSignature}
          missingIndex={taskKind === 'complete' ? missingIndex : undefined}
          showLabels={graphicMode === 'symbols-labels'}
          readingMethod={readingMethod}
          targetBarNumber={targetBarNumber}
          dottedIndices={dottedIndices}
          showClef
          printMode={printMode}
        />
      )}

      {hidePattern && (
        <p className="beat-cell-workshop__hint">
          Escucha el patrón y elige la célula rítmica correcta para cada tiempo del compás.
        </p>
      )}

      <div className="beat-cell-workshop__meter" aria-hidden>
        <span className="beat-cell-workshop__meter-sig">{timeSignature}</span>
        {targetBarNumber ? <span className="beat-cell-workshop__meter-bar">Compás {targetBarNumber}</span> : null}
      </div>

      <div className="beat-cell-workshop__grid" role="group" aria-label="Tiempos del compás">
          {beatCells.map((cell, index) => {
          const isBlank = blankIndices.includes(index);
          const slotKey = String(index);
          const value = values[slotKey] ?? '';
          const slotFeedback = feedback?.[slotKey];
          const glyphToken = (cell.tokens[0] ?? 'Q') as MusicalToken;
          const tied = cell.modifiers?.isTiedToNext === true;

          if (!isBlank && taskKind !== 'syllables') {
            return (
              <div
                key={index}
                className={`beat-cell-workshop__slot beat-cell-workshop__slot--given${
                  cell.modifiers?.isSpanContinuation ? ' beat-cell-workshop__slot--span-cont' : ''
                }`}
              >
                {!cell.modifiers?.isSpanContinuation && (
                  <span className="beat-cell-workshop__beat-label">Tiempo {index + 1}</span>
                )}
                <div className="beat-cell-workshop__card">
                  {cell.modifiers?.isSpanContinuation ? (
                    <span className="beat-cell-workshop__tie" title="Continúa la figura anterior">
                      ⌒
                    </span>
                  ) : (
                    <>
                      <VexRhythmGlyph token={glyphToken} size={printMode ? 28 : 32} color={printMode ? '#111' : '#e8e4dc'} />
                      <span className="beat-cell-workshop__name">{cell.name}</span>
                      <span className="beat-cell-workshop__takadimi">{cell.takadimi}</span>
                      {tied && <span className="beat-cell-workshop__tie" title="Ligadura">⌒</span>}
                      {cell.modifiers?.hasDot && (
                        <span className="beat-cell-workshop__dot" title="Puntillo">·</span>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          }

          const selected = value ? lookupCellById(value) : undefined;

          return (
            <div
              key={index}
              className={`beat-cell-workshop__slot beat-cell-workshop__slot--input${
                slotFeedback === 'correct'
                  ? ' beat-cell-workshop__slot--correct'
                  : slotFeedback === 'wrong'
                    ? ' beat-cell-workshop__slot--wrong'
                    : ''
              }`}
            >
              <span className="beat-cell-workshop__beat-label">Tiempo {index + 1}</span>
              <div className="beat-cell-workshop__card beat-cell-workshop__card--blank">
                {isSyllables ? (
                  <>
                    <VexRhythmGlyph token={glyphToken} size={28} color={printMode ? '#111' : '#e8e4dc'} />
                    <input
                      type="text"
                      className="beat-cell-workshop__syllable-input"
                      disabled={disabled}
                      value={value}
                      placeholder="Ta-ka-di…"
                      aria-label={`Sílabas tiempo ${index + 1}`}
                      onChange={(e) => onSlotChange(index, e.target.value)}
                    />
                    {!disabled && slotFeedback === 'wrong' && (
                      <span className="beat-cell-workshop__expected">{cell.takadimi}</span>
                    )}
                  </>
                ) : printMode ? (
                  <>
                    <VexRhythmGlyph token={glyphToken} size={24} color="#111" />
                    <div className="beat-cell-workshop__print-blank" aria-hidden />
                    <span className="beat-cell-workshop__name beat-cell-workshop__name--muted">{cell.name}</span>
                  </>
                ) : (
                  <>
                    <select
                      className="beat-cell-workshop__select"
                      disabled={disabled}
                      value={value}
                      aria-label={`Célula rítmica tiempo ${index + 1}`}
                      onChange={(e) => onSlotChange(index, e.target.value)}
                    >
                      <option value="">Elegir célula…</option>
                      {selectPalette.map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {cellToOptionLabel(opt, readingMethod)}
                        </option>
                      ))}
                    </select>
                    {selected && (
                      <span className="beat-cell-workshop__takadimi">{selected.takadimi}</span>
                    )}
                    {!disabled && slotFeedback === 'wrong' && (
                      <span className="beat-cell-workshop__expected">{cell.name}</span>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!disabled && !printMode && !isSyllables && taskKind === 'complete' && cellOptions?.length ? (
        <div className="beat-cell-workshop__palette" aria-label="Alternativas rítmicas">
          <span className="beat-cell-workshop__palette-label">Opciones:</span>
          {mcqPalette.map((cell) => (
            <button
              key={cell.id}
              type="button"
              className="beat-cell-workshop__palette-chip"
              title={cellToOptionLabel(cell, readingMethod)}
              disabled={disabled}
              onClick={() => {
                const target = blankIndices.find((i) => !values[String(i)]) ?? blankIndices[0];
                if (target !== undefined) onSlotChange(target, cell.id);
              }}
            >
              <VexRhythmGlyph token={(cell.tokens[0] ?? 'Q') as MusicalToken} size={22} color="#e8e4dc" />
              <span className="beat-cell-workshop__chip-name">{cell.name}</span>
            </button>
          ))}
        </div>
      ) : !disabled && !printMode && !isSyllables ? (
        <div className="beat-cell-workshop__palette" aria-label="Paleta de células rítmicas">
          <span className="beat-cell-workshop__palette-label">Células:</span>
          {palette.map((cell) => (
            <button
              key={cell.id}
              type="button"
              className="beat-cell-workshop__palette-chip"
              title={cellToOptionLabel(cell, readingMethod)}
              disabled={disabled}
              onClick={() => {
                const nextBlank = blankIndices.find((i) => !values[String(i)]);
                if (nextBlank !== undefined) onSlotChange(nextBlank, cell.id);
              }}
            >
              <VexRhythmGlyph token={(cell.tokens[0] ?? 'Q') as MusicalToken} size={22} color="#e8e4dc" />
              <span className="beat-cell-workshop__chip-name">{cell.name}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Tira compacta de células por tiempo (proyector / host). */
export function BeatCellStrip({
  beatCells,
  timeSignature = '4/4',
  targetBarNumber,
}: {
  beatCells: RhythmicCell[];
  timeSignature?: TimeSignature;
  targetBarNumber?: number;
}) {
  return (
    <div className="beat-cell-strip">
      <div className="beat-cell-strip__header">
        <span>{timeSignature}</span>
        {targetBarNumber ? <span>· Compás {targetBarNumber}</span> : null}
      </div>
      <div className="beat-cell-strip__row">
        {beatCells.map((cell, i) => (
          <div key={i} className="beat-cell-strip__item">
            <span className="beat-cell-strip__num">{i + 1}</span>
            <VexRhythmGlyph token={(cell.tokens[0] ?? 'Q') as MusicalToken} size={28} color="#e8e4dc" />
            <span className="beat-cell-strip__name">{cell.name}</span>
            <span className="beat-cell-strip__syllable">{cell.takadimi}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export { WORKSHOP_PALETTE_IDS, CELL_REGISTRY };
